import React, { useEffect, useMemo, useState, useContext, useCallback } from 'react';
import { Typography, Table, Card, Statistic, Row, Col, Segmented, Space, Select, Tag } from 'antd';
import api from '../api';
import { UserContext } from '../App';

function computeSpans(data, keyFn) {
  const spans = new Array(data.length).fill(0);
  let i = 0;
  while (i < data.length) {
    let j = i + 1;
    while (j < data.length && keyFn(data[j]) === keyFn(data[i])) j++;
    spans[i] = j - i;
    i = j;
  }
  return spans;
}

function fmtPct(grads, formerTrainee) {
  const denom = grads + formerTrainee;
  if (!denom) return '—';
  return `${(grads / denom * 100).toFixed(1)}%`;
}

function fmtFillPct(numerator, orderCount) {
  if (!orderCount) return '—';
  return `${(numerator / orderCount * 100).toFixed(1)}%`;
}

function orderFillNumerator(r) {
  return r.OrientationFallout + r.FormerTrainee + r.FormerEmployee + r.InProduction + r.InterTransfer;
}

function fmtTenure(months) {
  if (months == null) return '—';
  const m = Math.round(months);
  return `${Math.floor(m / 12)}y ${m % 12}m`;
}

function avgTenure(sumMonths, employees) {
  return sumMonths != null && employees > 0 ? sumMonths / employees : null;
}

function statusTag(status) {
  if (!status) return null;
  const s = status.toLowerCase();
  if (s === 'active') return <Tag color="green">Active</Tag>;
  if (s.includes('leave')) return <Tag color="orange">On Leave</Tag>;
  return <Tag>{status}</Tag>;
}

/**
 * `embedded` renders without the heading or filter controls, scoped by the
 * team passed in, and `sections` picks which blocks to draw. The Campaign
 * Summary's Census tab asks for two of the four.
 *
 * sections: 'stats' | 'recruitment' | 'byJob' | 'byTeamJob'. Omitted means all
 * of them, which is what the standalone page wants.
 */
export default function EmployeeCensusPage({ embedded = false, teamId: teamIdProp = null, sections = null }) {
  const currentUser = useContext(UserContext);
  // App Job is forced when embedded: the tab is defined as Team & App Job, and
  // a grouping toggle the tab does not show should not be able to change it.
  const [jobMode,      setJobMode]      = useState('app');
  const [teamId,       setTeamId]       = useState(embedded ? teamIdProp : null);

  const show = (name) => !sections || sections.includes(name);

  useEffect(() => {
    if (!embedded) return;
    setTeamId(teamIdProp);
  }, [embedded, teamIdProp]);
  const [teams,        setTeams]        = useState([]);
  const [headcount,    setHeadcount]    = useState([]);
  const [hcLoading,    setHcLoading]    = useState(true);
  const [recruitment,  setRecruitment]  = useState([]);
  const [recLoading,   setRecLoading]   = useState(true);
  const [hcView,       setHcView]       = useState('summary');
  const [employees,    setEmployees]    = useState([]);
  const [empLoading,   setEmpLoading]   = useState(false);

  useEffect(() => {
    api.get('/api/teams?my_teams=1&no_client_users=1').then((r) => setTeams(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setHcLoading(true);
    const params = { mode: jobMode };
    if (teamId) params.team_id = teamId;
    api.get('/api/census/headcount', { params })
      .then((r) => setHeadcount(r.data))
      .catch(() => {})
      .finally(() => setHcLoading(false));
  }, [jobMode, teamId]);

  useEffect(() => {
    setRecLoading(true);
    const params = {};
    if (teamId) params.team_id = teamId;
    api.get('/api/census/active-recruitment', { params })
      .then((r) => setRecruitment(r.data))
      .catch(() => {})
      .finally(() => setRecLoading(false));
  }, [teamId]);

  useEffect(() => {
    if (hcView !== 'employees') return;
    setEmpLoading(true);
    const params = { mode: jobMode };
    if (teamId) params.team_id = teamId;
    api.get('/api/census/headcount-employees', { params })
      .then((r) => setEmployees(r.data))
      .catch(() => {})
      .finally(() => setEmpLoading(false));
  }, [hcView, jobMode, teamId]);

  // ── By-Job summary (aggregated from headcount rows) ──────────────────────────
  const headcountByJob = useMemo(() => {
    const map = {};
    for (const r of headcount) {
      const key = r.Job;
      if (!map[key]) map[key] = {
        Job: key, QtyActive: 0, QtyOnLeave: 0, Employees: 0, FTE: 0,
        SumTenureMonths: null,
        Tenure_Lt3M: 0, Tenure_3M_1Y: 0, Tenure_1Y_2Y: 0, Tenure_2Y_4Y: 0, Tenure_Gte4Y: 0,
        InTrainingOrOffer: 0,
      };
      map[key].QtyActive  += r.QtyActive  ?? 0;
      map[key].QtyOnLeave += r.QtyOnLeave ?? 0;
      map[key].Employees  += r.Employees  ?? 0;
      map[key].FTE        += Number(r.FTE) || 0;
      if (r.SumTenureMonths != null) {
        map[key].SumTenureMonths = (map[key].SumTenureMonths ?? 0) + r.SumTenureMonths;
      }
      map[key].Tenure_Lt3M       += r.Tenure_Lt3M       ?? 0;
      map[key].Tenure_3M_1Y      += r.Tenure_3M_1Y      ?? 0;
      map[key].Tenure_1Y_2Y      += r.Tenure_1Y_2Y      ?? 0;
      map[key].Tenure_2Y_4Y      += r.Tenure_2Y_4Y      ?? 0;
      map[key].Tenure_Gte4Y      += r.Tenure_Gte4Y      ?? 0;
      map[key].InTrainingOrOffer += r.InTrainingOrOffer  ?? 0;
    }
    return Object.values(map).sort((a, b) => a.Job.localeCompare(b.Job));
  }, [headcount]);

  const byJobTotalActive    = headcountByJob.reduce((s, r) => s + r.QtyActive,  0);
  const byJobTotalOnLeave   = headcountByJob.reduce((s, r) => s + r.QtyOnLeave, 0);
  const byJobTotalEmployees = headcountByJob.reduce((s, r) => s + r.Employees,  0);
  const byJobTotalFTE       = headcountByJob.reduce((s, r) => s + r.FTE,        0);
  const byJobTotalLt3M      = headcountByJob.reduce((s, r) => s + r.Tenure_Lt3M,  0);
  const byJobTotal3M1Y      = headcountByJob.reduce((s, r) => s + r.Tenure_3M_1Y, 0);
  const byJobTotal1Y2Y      = headcountByJob.reduce((s, r) => s + r.Tenure_1Y_2Y, 0);
  const byJobTotal2Y4Y      = headcountByJob.reduce((s, r) => s + r.Tenure_2Y_4Y, 0);
  const byJobTotalGte4Y     = headcountByJob.reduce((s, r) => s + r.Tenure_Gte4Y, 0);
  const byJobTotalSum       = headcountByJob.reduce((s, r) => s + (r.SumTenureMonths ?? 0), 0);
  const byJobTotalAvg       = avgTenure(byJobTotalSum, byJobTotalEmployees);

  // ── Dept+Job headcount table ─────────────────────────────────────────────────
  const hcDeptSpans    = computeSpans(headcount, (r) => r.Department);
  const totalActive    = headcount.reduce((s, r) => s + (r.QtyActive   ?? 0), 0);
  const totalOnLeave   = headcount.reduce((s, r) => s + (r.QtyOnLeave  ?? 0), 0);
  const totalEmployees = headcount.reduce((s, r) => s + (r.Employees   ?? 0), 0);
  const totalFTE       = headcount.reduce((s, r) => s + (Number(r.FTE) || 0), 0);
  const totalLt3M      = headcount.reduce((s, r) => s + (r.Tenure_Lt3M  ?? 0), 0);
  const total3M1Y      = headcount.reduce((s, r) => s + (r.Tenure_3M_1Y ?? 0), 0);
  const total1Y2Y      = headcount.reduce((s, r) => s + (r.Tenure_1Y_2Y ?? 0), 0);
  const total2Y4Y      = headcount.reduce((s, r) => s + (r.Tenure_2Y_4Y ?? 0), 0);
  const totalGte4Y     = headcount.reduce((s, r) => s + (r.Tenure_Gte4Y ?? 0), 0);
  const totalSum       = headcount.reduce((s, r) => r.SumTenureMonths != null ? s + r.SumTenureMonths : s, 0);
  const totalAvg       = avgTenure(totalSum, totalEmployees);

  const agentRow              = headcountByJob.find((r) => r.Job === 'Agent');
  const agentCount            = agentRow?.QtyActive ?? 0;
  const agentInTrainingOrOffer = agentRow?.InTrainingOrOffer ?? 0;
  const supportAgentCount     = headcountByJob.find((r) => r.Job === 'Support Agent')?.QtyActive ?? 0;
  const frontlinePct      = totalActive > 0 ? ((agentCount + supportAgentCount) / totalActive * 100).toFixed(1) : null;

  const recruitmentColumns = [
    { title: 'Start Date',           dataIndex: 'StartDate',           key: 'StartDate',     width: 110 },
    { title: 'Team',                 dataIndex: 'TeamName',            key: 'TeamName',      width: 180 },
    { title: 'Job',                  dataIndex: 'JobName',             key: 'JobName',       width: 140, render: (v) => v || '—' },
    { title: 'Order Count FTE',       dataIndex: 'OrderCount',          key: 'OrderCount',    width: 130, align: 'right',
      render: (v) => v != null ? Number(v).toFixed(1) : '—' },
    {
      title: 'Order Fill % FTE', key: 'OrderFillPct', width: 130, align: 'right',
      render: (_, r) => fmtFillPct(r.OrderFillFTENumerator ?? 0, r.OrderCount),
    },
    { title: 'Grad Target FTE',      dataIndex: 'GraduationTargetFTE', key: 'GradTarget',    width: 130, align: 'right',
      render: (v) => v != null ? Number(v).toFixed(1) : '—' },
    { title: 'Accepted Offer',       dataIndex: 'AcceptedOffer',       key: 'AcceptedOffer',       width: 120, align: 'right' },
    { title: 'Rescinded Offer',      dataIndex: 'RescindedOffer',      key: 'RescindedOffer',      width: 130, align: 'right' },
    { title: 'No Show',              dataIndex: 'NoShow',              key: 'NoShow',              width: 90,  align: 'right' },
    { title: 'Orientation Fallout',  dataIndex: 'OrientationFallout',  key: 'OrientationFallout',  width: 150, align: 'right' },
    { title: 'In Training',          dataIndex: 'InTraining',          key: 'InTraining',          width: 105, align: 'right' },
    { title: 'Former Trainee',       dataIndex: 'FormerTrainee',       key: 'FormerTrainee',       width: 120, align: 'right' },
    { title: 'In Production',        dataIndex: 'InProduction',        key: 'InProduction',        width: 115, align: 'right' },
    { title: 'Former Employee',      dataIndex: 'FormerEmployee',      key: 'FormerEmployee',      width: 130, align: 'right' },
    { title: 'Inter Transfer',       dataIndex: 'InterTransfer',       key: 'InterTransfer',       width: 115, align: 'right' },
    {
      title: 'Grads', key: 'Grads', width: 90, align: 'right',
      render: (_, r) => r.InProduction + r.FormerEmployee + r.InterTransfer,
    },
    {
      title: 'Grads FTE', key: 'GradFTE', width: 95, align: 'right',
      render: (_, r) => (r.GradFTE ?? 0).toFixed(1),
    },
    {
      title: 'Grad %', key: 'GradPct', width: 90, align: 'right',
      render: (_, r) => {
        const grads = r.InProduction + r.FormerEmployee + r.InterTransfer;
        const denom = r.OrientationFallout + r.FormerTrainee + r.InProduction + r.FormerEmployee + r.InterTransfer;
        return fmtFillPct(grads, denom);
      },
    },
    {
      title: 'FTE Grads Above Target', key: 'GradsAboveTarget', width: 165, align: 'right',
      render: (_, r) => {
        if (r.GraduationTargetFTE == null) return '—';
        const val = (r.GradFTE ?? 0) - Number(r.GraduationTargetFTE);
        const color = val > 0 ? '#52c41a' : val < 0 ? '#ff4d4f' : undefined;
        return <span style={{ color }}>{val > 0 ? `+${val.toFixed(1)}` : val.toFixed(1)}</span>;
      },
    },
  ];

  const TENURE_COLS = [
    { title: 'Avg Tenure',    key: 'AvgTenure',     width: 100, align: 'right',
      render: (_, r) => fmtTenure(avgTenure(r.SumTenureMonths, r.Employees)) },
    { title: '< 3 Mo',       dataIndex: 'Tenure_Lt3M',  key: 'Lt3M',   width: 75,  align: 'right' },
    { title: '3 Mo–1 Yr',    dataIndex: 'Tenure_3M_1Y', key: '3M1Y',   width: 85,  align: 'right' },
    { title: '1–2 Yrs',      dataIndex: 'Tenure_1Y_2Y', key: '1Y2Y',   width: 75,  align: 'right' },
    { title: '2–4 Yrs',      dataIndex: 'Tenure_2Y_4Y', key: '2Y4Y',   width: 75,  align: 'right' },
    { title: '>= 4 Yrs',     dataIndex: 'Tenure_Gte4Y', key: 'Gte4Y',  width: 80,  align: 'right' },
  ];

  const byJobColumns = [
    { title: jobMode === 'app' ? 'App Job' : 'Paycor Job Title', dataIndex: 'Job', key: 'Job' },
    { title: 'Qty Active',      dataIndex: 'QtyActive',  key: 'QtyActive',  width: 110, align: 'right' },
    { title: 'Qty On Leave',    dataIndex: 'QtyOnLeave', key: 'QtyOnLeave', width: 120, align: 'right' },
    { title: 'Total Employees', dataIndex: 'Employees',  key: 'Employees',  width: 140, align: 'right' },
    { title: 'FTE Qty', dataIndex: 'FTE', key: 'FTE', width: 90, align: 'right',
      render: (v) => v != null ? Number(v).toFixed(1) : '—' },
    ...TENURE_COLS,
  ];

  const headcountColumns = [
    { title: jobMode === 'app' ? 'Team' : 'Department', dataIndex: 'Department', key: 'Department', width: 220,
      render: (v, _, i) => ({ children: v, props: { rowSpan: hcDeptSpans[i] } }) },
    { title: jobMode === 'app' ? 'App Job' : 'Paycor Job Title', dataIndex: 'Job', key: 'Job', width: 160 },
    { title: 'Qty Active',      dataIndex: 'QtyActive',  key: 'QtyActive',  width: 95,  align: 'right' },
    { title: 'Qty On Leave',    dataIndex: 'QtyOnLeave', key: 'QtyOnLeave', width: 110, align: 'right' },
    { title: 'Total Employees', dataIndex: 'Employees',  key: 'Employees',  width: 130, align: 'right' },
    { title: 'FTE Qty', dataIndex: 'FTE', key: 'FTE', width: 90, align: 'right',
      render: (v) => v != null ? Number(v).toFixed(1) : '—' },
    ...TENURE_COLS,
  ];

  // Employee list view
  const empDeptSpans = useMemo(() => computeSpans(employees, (r) => r.Department), [employees]);
  const empJobSpans  = useMemo(() => computeSpans(employees, (r) => `${r.Department}|||${r.Job}`), [employees]);

  const headcountEmployeeColumns = [
    { title: jobMode === 'app' ? 'Team' : 'Department', dataIndex: 'Department', key: 'Department', width: 220,
      render: (v, _, i) => ({ children: v, props: { rowSpan: empDeptSpans[i], style: { verticalAlign: 'top' } } }) },
    { title: jobMode === 'app' ? 'App Job' : 'Paycor Job Title', dataIndex: 'Job', key: 'Job', width: 180,
      render: (v, _, i) => ({ children: v, props: { rowSpan: empJobSpans[i], style: { verticalAlign: 'top' } } }) },
    { title: 'Employee Name', dataIndex: 'EmployeeName', key: 'EmployeeName' },
    { title: 'Status', dataIndex: 'Status', key: 'Status', width: 110,
      render: (v) => statusTag(v) },
    { title: 'Full Time', dataIndex: 'FullTime', key: 'FullTime', width: 100, align: 'center',
      render: (v) => v ? <Tag color="blue">Full Time</Tag> : <Tag>Part Time</Tag> },
    // No sorter: Team and App Job are drawn with rowSpan, and reordering the
    // rows would leave those merged cells spanning the wrong people.
    { title: 'Tenure', dataIndex: 'TenureMonths', key: 'TenureMonths', width: 100, align: 'right',
      render: (v) => fmtTenure(v) },
  ];

  return (
    <>
      {!embedded && (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Employee Census</Typography.Title>
        <Space align="center">
          {teams.length > 0 && (
            <Select
              allowClear
              placeholder="All Teams"
              style={{ width: 200 }}
              value={teamId}
              onChange={setTeamId}
              options={teams.map((t) => ({ value: t.ID, label: t.Name }))}
            />
          )}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>Job grouping:</Typography.Text>
          <Segmented
            value={jobMode}
            onChange={setJobMode}
            options={[
              { label: 'App Job',    value: 'app'    },
              { label: 'Paycor Job', value: 'paycor' },
            ]}
          />
        </Space>
      </div>
      )}

      {show('stats') && (
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col>
          <Card size="small" style={{ minWidth: 160 }}>
            <Statistic title="Qty Active"   value={totalActive}  loading={hcLoading} />
          </Card>
        </Col>
        <Col>
          <Card size="small" style={{ minWidth: 160 }}>
            <Statistic title="Qty On Leave" value={totalOnLeave} loading={hcLoading} />
          </Card>
        </Col>
        {jobMode === 'app' && (
          <>
            <Col>
              <Card size="small" style={{ minWidth: 180 }}>
                <Statistic title="Agents" value={agentCount} loading={hcLoading} />
                {!hcLoading && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {agentInTrainingOrOffer} In Training / Offer Accepted
                  </Typography.Text>
                )}
              </Card>
            </Col>
            <Col>
              <Card size="small" style={{ minWidth: 160 }}>
                <Statistic title="Support Agents" value={supportAgentCount} loading={hcLoading} />
              </Card>
            </Col>
            <Col>
              <Card size="small" style={{ minWidth: 200 }}>
                <Statistic title="% Staff in Agent / Support" value={frontlinePct ?? '—'} suffix={frontlinePct != null ? '%' : ''} loading={hcLoading} />
              </Card>
            </Col>
          </>
        )}
      </Row>
      )}

      {show('recruitment') && (
      <>
      <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
        Active Recruitment <Typography.Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>(last 30 days + next 30 days)</Typography.Text>
      </Typography.Title>
      <Table
        rowKey={(r) => `${r.StartDate}-${r.TeamName}`}
        dataSource={recruitment}
        columns={recruitmentColumns}
        loading={recLoading}
        size="small"
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'No recruitment activity in the last or next 30 days.' }}
        style={{ marginBottom: 24 }}
      />
      </>
      )}

      {show('byJob') && (
      <>
      <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
        Active Headcount By Job
      </Typography.Title>
      <Table
        rowKey={(r) => r.Job}
        dataSource={headcountByJob}
        columns={byJobColumns}
        loading={hcLoading}
        size="small"
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'No active employees found.' }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right"><strong>{byJobTotalActive.toLocaleString()}</strong></Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right"><strong>{byJobTotalOnLeave.toLocaleString()}</strong></Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right"><strong>{byJobTotalEmployees.toLocaleString()}</strong></Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="right"><strong>{byJobTotalFTE.toFixed(1)}</strong></Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="right"><strong>{fmtTenure(byJobTotalAvg)}</strong></Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="right"><strong>{byJobTotalLt3M}</strong></Table.Summary.Cell>
              <Table.Summary.Cell index={7} align="right"><strong>{byJobTotal3M1Y}</strong></Table.Summary.Cell>
              <Table.Summary.Cell index={8} align="right"><strong>{byJobTotal1Y2Y}</strong></Table.Summary.Cell>
              <Table.Summary.Cell index={9} align="right"><strong>{byJobTotal2Y4Y}</strong></Table.Summary.Cell>
              <Table.Summary.Cell index={10} align="right"><strong>{byJobTotalGte4Y}</strong></Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
      </>
      )}

      {show('byTeamJob') && (
      <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 16, marginBottom: 8 }}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          Active Headcount by {jobMode === 'app' ? 'Team & App Job' : 'Department & Job'}
        </Typography.Title>
        <Segmented
          size="small"
          value={hcView}
          onChange={setHcView}
          options={[
            { label: 'Summary',       value: 'summary' },
            { label: 'Employee List', value: 'employees' },
          ]}
        />
      </div>

      {hcView === 'summary' ? (
        <Table
          rowKey={(_, i) => `hc-${i}`}
          dataSource={headcount}
          columns={headcountColumns}
          loading={hcLoading}
          size="small"
          pagination={false}
          scroll={{ x: 'max-content', y: 400 }}
          locale={{ emptyText: 'No active employees found.' }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2}><strong>Total</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right"><strong>{totalActive.toLocaleString()}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right"><strong>{totalOnLeave.toLocaleString()}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right"><strong>{totalEmployees.toLocaleString()}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right"><strong>{totalFTE.toFixed(1)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="right"><strong>{fmtTenure(totalAvg)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={7} align="right"><strong>{totalLt3M}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={8} align="right"><strong>{total3M1Y}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={9} align="right"><strong>{total1Y2Y}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={10} align="right"><strong>{total2Y4Y}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={11} align="right"><strong>{totalGte4Y}</strong></Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      ) : (
        <Table
          rowKey={(_, i) => `emp-${i}`}
          dataSource={employees}
          columns={headcountEmployeeColumns}
          loading={empLoading}
          size="small"
          pagination={false}
          scroll={{ x: 'max-content', y: 500 }}
          locale={{ emptyText: 'No active employees found.' }}
        />
      )}
      </>
      )}
    </>
  );
}
